<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import ToolRail from '@/components/ToolRail/ToolRail.vue'
import ToolRailItem from '@/components/ToolRail/ToolRailItem.vue'
import ToolRailSection from '@/components/ToolRail/ToolRailSection.vue'
import ToolRailDropdown from '@/components/ToolRail/ToolRailDropdown.vue'
import ColumnsDropdown from '@/components/ToolRail/ColumnsDropdown.vue'
import SaveSearchDropdown from '@/components/EventSearch/SaveSearchDropdown.vue'
import QueryGuide from '@/components/Query/QueryGuide.vue'
import { useMainStore } from '@/stores/main'
import { getLogTypeColor } from '@/constants/logTypes'

const mainStore = useMainStore()
const route = useRoute()

const tableLink = computed(() => ({ path: '/search/event/table', query: route.query }))
const aggregationsLink = computed(() => ({ path: '/search/event/aggregations', query: route.query }))
const mapLink = computed(() => ({ path: '/search/event/map', query: route.query }))
const historyLink = computed(() => ({ path: '/search/event/history', query: route.query }))

const props = defineProps({
  hasExecutedQuery: {
    type: Boolean,
    default: false
  },
  canExport: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  exportLoading: {
    type: Boolean,
    default: false
  },
  logTypeDistribution: {
    type: Array,
    default: () => []
  },
  // Column controller for the currently displayed log type (null = no columns).
  columns: {
    type: Object,
    default: null
  },
  // Saved search props
  savedSearchId: {
    type: String,
    default: null
  },
  savedSearchName: {
    type: String,
    default: ''
  },
  savedSearchDescription: {
    type: String,
    default: ''
  },
  savedSearchQuery: {
    type: String,
    default: ''
  },
  currentQuery: {
    type: String,
    default: ''
  },
  saveLoading: {
    type: Boolean,
    default: false
  },
  deleteLoading: {
    type: Boolean,
    default: false
  },
  // Subscription props
  isSubscribed: {
    type: Boolean,
    default: false
  },
  subscriptionLoading: {
    type: Boolean,
    default: false
  },
  hasGeoAnnotations: {
    type: Boolean,
    default: false
  }
})

const selectedFormat = ref('jsonl')
const emit = defineEmits(['update:expanded', 'export', 'export-log-type', 'save-search', 'overwrite-search', 'delete-search', 'subscribe', 'unsubscribe'])

const mapEnabled = computed(() => props.hasExecutedQuery && props.hasGeoAnnotations)

function formatCount(count) {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
  return count.toLocaleString()
}

function handleExportAll() {
  emit('export', selectedFormat.value)
}

function handleExportLogType(logType) {
  emit('export-log-type', logType, selectedFormat.value)
}

function openFieldGuide() {
  mainStore.show_slideover(QueryGuide, {}, { width: 40 }, 'Query Guide')
}
</script>

<template>
  <ToolRail
    storage-key="app-tool-sidebar-expanded"
    aria-label="Search tools"
    @update:expanded="emit('update:expanded', $event)"
  >
    <!-- Section: Views -->
    <ToolRailSection label="Views" :divider="false" />

    <ToolRailItem
      :icon="['fas', 'table']"
      label="table"
      :to="tableLink"
      :disabled="!hasExecutedQuery"
    />
    <ToolRailItem
      :icon="['fas', 'chart-bar']"
      label="aggregations"
      :to="aggregationsLink"
      :disabled="!hasExecutedQuery"
    />
    <ToolRailItem
      :icon="['fas', 'globe']"
      label="map"
      :to="mapLink"
      :disabled="!mapEnabled"
    />

    <!-- Section: Data -->
    <ToolRailSection label="Data" />

    <ColumnsDropdown :columns="columns" />

    <ToolRailDropdown
      :icon="['fas', 'download']"
      label="export"
      header-label="Export"
      header-subtext="max 10,000 documents"
      :disabled="!canExport || loading"
      :loading="exportLoading"
      loading-text="Exporting..."
    >
      <div class="format-toggle">
        <button
          class="format-btn"
          :class="{ active: selectedFormat === 'jsonl' }"
          @click="selectedFormat = 'jsonl'"
        >JSONL</button>
        <button
          class="format-btn"
          :class="{ active: selectedFormat === 'csv' }"
          @click="selectedFormat = 'csv'"
        >CSV</button>
      </div>

      <div class="dropdown-divider"></div>

      <div class="dropdown-item" @click="handleExportAll">
        <font-awesome-icon :icon="['fas', 'file-export']" class="item-icon" />
        <span>Export all</span>
      </div>

      <div v-if="logTypeDistribution.length > 0" class="dropdown-divider"></div>

      <div v-if="logTypeDistribution.length > 0" class="dropdown-section">By log type</div>

      <div
        v-for="dist in logTypeDistribution"
        :key="dist.log_type"
        class="dropdown-item log-type-item"
        @click="handleExportLogType(dist.log_type)"
      >
        <span
          class="log-type-indicator"
          :style="{ backgroundColor: getLogTypeColor(dist.log_type) }"
        ></span>
        <span class="log-type-name">{{ dist.log_type }}</span>
        <span class="log-type-count" :class="{ 'over-limit': dist.count > 10000 }">
          {{ formatCount(dist.count) }}
          <font-awesome-icon
            v-if="dist.count > 10000"
            :icon="['fas', 'triangle-exclamation']"
            class="warning-icon"
          />
        </span>
      </div>
    </ToolRailDropdown>

    <!-- Section: Search -->
    <ToolRailSection label="Search" />

    <ToolRailItem
      :icon="['fas', 'clock-rotate-left']"
      label="query history"
      :to="historyLink"
    />
    <ToolRailItem
      :icon="['fas', 'question']"
      label="query guide"
      @select="openFieldGuide"
    />

    <SaveSearchDropdown
      :disabled="!hasExecutedQuery"
      :query="currentQuery"
      :saved-search-id="savedSearchId"
      :saved-search-name="savedSearchName"
      :saved-search-description="savedSearchDescription"
      :saved-search-query="savedSearchQuery"
      :loading="saveLoading"
      :delete-loading="deleteLoading"
      :is-subscribed="isSubscribed"
      :subscription-loading="subscriptionLoading"
      @save="$emit('save-search', $event)"
      @overwrite="$emit('overwrite-search', $event)"
      @delete="$emit('delete-search')"
      @subscribe="$emit('subscribe')"
      @unsubscribe="$emit('unsubscribe')"
    />
  </ToolRail>
</template>

<!-- Unscoped styles for the export dropdown menu content (teleported to body).
     Namespaced under .dropdown-panel so they don't leak. -->
<style>
.dropdown-panel .log-type-item {
  padding: 0.5rem 0.75rem;
}

.dropdown-panel .log-type-indicator {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
}

.dropdown-panel .log-type-name {
  flex: 1;
}

.dropdown-panel .log-type-count {
  font-size: 0.75rem;
  color: var(--text-faint);
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.dropdown-panel .log-type-count.over-limit {
  color: var(--color-warning);
}

.dropdown-panel .warning-icon {
  font-size: 0.7rem;
}
</style>
